import { Link, Node, PinType } from "./diagram/diagram.ts"
import { PinColorType } from "../../configs/pinColors.ts"
import { memo, useMemo } from "react"
import { DiagramPin } from "./DiagramPin.tsx"

import { useSearchAddress } from "../../hooks/useSearchAddress.ts"

interface DiagramPinsProps {
    graph: {
        nodes: Node[],
        links: Link[]
    },
    width: number
}

export const DiagramPins = memo(({ graph, width } : DiagramPinsProps) => {
    const { searchResult } = useSearchAddress()

    const composePin = (subj: Node | Link, allPins: PinType[], color: PinColorType) => {
        const pin: PinType = { ...subj as PinType }
        pin.pinColor = color
        const duplicatePinsCount = allPins.filter((a) =>
            a.id === pin.id ||
            (a.id.split('-').length > 1 && a.id.split('-')[0] === pin.id.split('-')[0]) ||
            (a.id.split('-').length > 1 && a.id.split('-')[1] === pin.id.split('-')[1])).length
        if (duplicatePinsCount) {
            pin.modifierX = duplicatePinsCount * 20
            pin.modifierY = 'targetAngle' in pin ? 0 : duplicatePinsCount * 20
        }
        pin.position = {
            x: (pin?.position?.x ??
                ((pin.source.depth! <= 0) ? pin?.source?.position?.x : pin?.target.position?.x))!,
            y: (pin?.position?.y ??
                ((pin.source.depth! <= 0) ? pin?.source?.position?.y : pin?.target.position?.y))!
        }
        pin.extent = {
            x: pin?.extent?.x ?? 105,
            y: pin?.extent?.y ?? 0
        }
        return pin
    }

    const pinsFound = useMemo(() => {
        const allKeys = Object.keys(searchResult.entitiesFound).concat(Object.keys(searchResult.edgesFound))
        if (allKeys.length) {
            const acc: PinType[] = []
            const maxLength = Math.max(graph.nodes.length, graph.links.length)
            for (const value of Object.values(searchResult.entitiesFound)) {
                for (let i = 0; i < maxLength; i++) {
                    if (graph.nodes[i] && graph.nodes[i].semanticData.ID === value.entityID) {
                        acc.push(composePin(graph.nodes[i], acc, value.color))
                    }
                }
            }
            for (const value of Object.values(searchResult.edgesFound)) {
                for (let i = 0; i < maxLength; i++) {
                    if (graph.links[i] && graph.links[i].id === value.edgeID) {
                        acc.push(composePin(graph.links[i], acc, value.color))
                    }
                }
            }
            return acc
        } else {
            return null
        }
    }, [searchResult, graph.nodes, graph.links])

    return pinsFound ? pinsFound.map((node) => (<DiagramPin
        key={node.id + node.pinColor + '_pin'}
        entityId={node?.semanticData?.ID ?? node.id}
        modX={node.modifierX ?? 0}
        modY={node.modifierY ?? 0}
        left={node.position!.x - node.extent!.x}
        right={node.position!.x + node.extent!.x}
        bottom={node.position!.y + node.extent!.y}
        width={width}
        color={node.pinColor}
    />)) : null
})
